import { ReadSpacesListDTO } from '@/modules/spaces/hooks/types'
import { useReadAvailableSpaceOwners } from '@/modules/spaces/hooks/useReadAvailableSpaceOwners'
import {
  Button2,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  IonIcon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@valuecase/ui-components'
import { useCallback, useMemo, useState } from 'react'
import GradientSpacesList from './GradientSpacesList'

type ChangeOwnerSpacesDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  selectedSpaces: ReadSpacesListDTO[]
  onOwnerChange: (ownerId: string) => Promise<void>
  isLoading: boolean
}

type OwnerOption = {
  value: string
  label: string
}

export function ChangeOwnerSpacesDialog({
  isOpen,
  onOpenChange,
  selectedSpaces,
  onOwnerChange,
  isLoading,
}: ChangeOwnerSpacesDialogProps) {
  const { availableSpaceOwners, isLoading: isLoadingOwners } = useReadAvailableSpaceOwners()
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>('')

  const handleOwnerChange = useCallback(async () => {
    if (!selectedOwnerId) return
    await onOwnerChange(selectedOwnerId)
    setSelectedOwnerId('')
  }, [selectedOwnerId, onOwnerChange])

  const ownerOptions = useMemo<OwnerOption[]>(() => {
    if (!availableSpaceOwners) return []
    return availableSpaceOwners.map((owner) => ({
      value: owner.id,
      label: `${owner.firstName} ${owner.lastName}`,
    }))
  }, [availableSpaceOwners])

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className='min-w-[580px]'>
        <DialogHeader className='flex items-center gap-2'>
          <IonIcon name='shuffle' className='w-10 h-10 text-blue-s4' />
          <DialogTitle>Change Owner</DialogTitle>
          <DialogDescription>
            Please select a user to take over{' '}
            {selectedSpaces.length === 1
              ? `the ${selectedSpaces[0].companyName} space`
              : `these ${selectedSpaces.length} spaces`}
            .
          </DialogDescription>
        </DialogHeader>
        <DialogBody className='bg-white border-none flex flex-col gap-4'>
          <GradientSpacesList spaces={selectedSpaces} />
          <Select value={selectedOwnerId} onValueChange={setSelectedOwnerId}>
            <SelectTrigger className='text-grey-s4' disabled={isLoadingOwners}>
              <SelectValue placeholder='Select new owner' />
            </SelectTrigger>
            <SelectContent>
              {ownerOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DialogBody>

        <div className='flex gap-3'>
          <Button2
            className='w-full'
            variant='outlined'
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button2>
          <Button2
            className='w-full'
            leadingIcon='shuffle'
            variant='solid'
            onClick={handleOwnerChange}
            disabled={isLoading || !selectedOwnerId}
          >
            {isLoading ? 'Changing...' : 'Change Owner'}
          </Button2>
        </div>
      </DialogContent>
    </Dialog>
  )
}
